import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../StockInward.css";
import {
  addMiscExpense,
  getEmployeeAttendance,
  getEmployeeMasters,
  getMiscExpenses,
  saveEmployeeAttendanceWeek,
} from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";

const NAME_COLUMN_WIDTH = 220;
const RATE_COLUMN_WIDTH = 140;
const DATE_COLUMN_WIDTH = 118;
const TOTAL_ATTENDANCE_COLUMN_WIDTH = 150;
const TOTAL_SALARY_COLUMN_WIDTH = 170;
const SALARY_PAID_COLUMN_WIDTH = 160;
const PENDING_SALARY_COLUMN_WIDTH = 160;
const PAYMENT_ACTION_COLUMN_WIDTH = 170;
const SALARY_EXPENSE_TITLE = "Salary";
const SALARY_EXPENSE_CATEGORY = "Master";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function todayYmd() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

function normalizeYmd(value) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  return raw;
}

function formatYmd(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function addDaysYmd(dateText, days) {
  const normalized = normalizeYmd(dateText) || todayYmd();
  const date = new Date(`${normalized}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatYmd(date);
}

function startOfWeek(dateText) {
  const normalized = normalizeYmd(dateText) || todayYmd();
  const date = new Date(`${normalized}T00:00:00`);
  const weekday = date.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  date.setDate(date.getDate() + offset);
  return formatYmd(date);
}

function currentWeekStart() {
  return startOfWeek(todayYmd());
}

function getWeekDays(weekStart) {
  const monday = startOfWeek(weekStart || currentWeekStart());
  return Array.from({ length: 7 }, (_, index) => {
    const dateText = addDaysYmd(monday, index);
    const date = new Date(`${dateText}T00:00:00`);
    return {
      date: dateText,
      weekdayShort: new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date),
      weekdayFull: new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date),
    };
  });
}

function formatWeekRange(weekDays) {
  if (!Array.isArray(weekDays) || weekDays.length === 0) return "-";
  return `${formatDisplayDate(weekDays[0].date)} to ${formatDisplayDate(weekDays[weekDays.length - 1].date)}`;
}

function formatUnits(value) {
  const numeric = Number(value) || 0;
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
}

function formatMoney(value) {
  return (Number(value) || 0).toFixed(2);
}

function normalizeEmployeeName(value) {
  return String(value || "").trim().toLowerCase();
}

function isEmployeeActive(employee) {
  return employee?.active !== false;
}

function getEmployeeJoiningDate(employee) {
  return normalizeYmd(employee?.joiningDate);
}

function isAttendanceDayLocked(employee, dateText) {
  const joiningDate = getEmployeeJoiningDate(employee);
  const targetDate = normalizeYmd(dateText);
  return Boolean(joiningDate && targetDate && targetDate < joiningDate);
}

function isYmdWithinRange(value, rangeStart, rangeEnd) {
  const normalized = normalizeYmd(value);
  if (!normalized) return false;
  if (rangeStart && normalized < rangeStart) return false;
  if (rangeEnd && normalized > rangeEnd) return false;
  return true;
}

function makeSalaryPaymentDraft() {
  return {
    open: false,
    employeeId: "",
    employeeName: "",
    selectedDates: [],
    notes: "",
  };
}

function buildDraft(employees, records, weekDays) {
  const recordMap = new Map(
    (Array.isArray(records) ? records : []).map((record) => [
      `${record?.employeeId || ""}|${record?.date || ""}`,
      record,
    ])
  );

  const next = {};
  for (const employee of Array.isArray(employees) ? employees : []) {
    const row = {};
    for (const day of Array.isArray(weekDays) ? weekDays : []) {
      const saved = recordMap.get(`${employee?.id || ""}|${day.date}`);
      if (!saved && isAttendanceDayLocked(employee, day.date)) {
        row[day.date] = "";
        continue;
      }
      row[day.date] =
        saved?.attendanceValue === null || saved?.attendanceValue === undefined
          ? "0"
          : formatUnits(saved.attendanceValue);
    }
    next[String(employee?.id || "")] = row;
  }
  return next;
}

function parseAttendanceValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return +numeric.toFixed(2);
}

export default function EmployeeAttendance({ currentUser }) {
  const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => currentWeekStart());
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [miscExpenses, setMiscExpenses] = useState([]);
  const [draft, setDraft] = useState({});
  const [query, setQuery] = useState("");
  const [attendanceTab, setAttendanceTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salaryPaymentSaving, setSalaryPaymentSaving] = useState(false);
  const [salaryPaymentDraft, setSalaryPaymentDraft] = useState(() => makeSalaryPaymentDraft());
  const refreshRequestRef = useRef(0);
  const attendanceTableScrollRef = useRef(null);

  const weekDays = useMemo(() => getWeekDays(selectedWeekStart), [selectedWeekStart]);
  const weekLabel = useMemo(() => formatWeekRange(weekDays), [weekDays]);
  const selectedWeekEnd = useMemo(
    () => (Array.isArray(weekDays) && weekDays.length > 0 ? weekDays[weekDays.length - 1].date : addDaysYmd(selectedWeekStart, 6)),
    [selectedWeekStart, weekDays]
  );
  const tableMinWidth = useMemo(
    () =>
      NAME_COLUMN_WIDTH +
      RATE_COLUMN_WIDTH +
      TOTAL_ATTENDANCE_COLUMN_WIDTH +
      TOTAL_SALARY_COLUMN_WIDTH +
      SALARY_PAID_COLUMN_WIDTH +
      PENDING_SALARY_COLUMN_WIDTH +
      PAYMENT_ACTION_COLUMN_WIDTH +
      weekDays.length * DATE_COLUMN_WIDTH,
    [weekDays.length]
  );

  const attendanceRecordMap = useMemo(() => {
    return new Map(
      (Array.isArray(attendanceRecords) ? attendanceRecords : []).map((record) => [
        `${record?.employeeId || ""}|${record?.date || ""}`,
        record,
      ])
    );
  }, [attendanceRecords]);

  const refresh = async (weekStartValue = selectedWeekStart) => {
    if (!isAdmin) return;
    const requestId = refreshRequestRef.current + 1;
    refreshRequestRef.current = requestId;
    setLoading(true);
    try {
      const [employeeList, attendanceResponse, miscExpenseList] = await Promise.all([
        getEmployeeMasters(),
        getEmployeeAttendance(weekStartValue),
        getMiscExpenses(),
      ]);
      if (requestId !== refreshRequestRef.current) {
        return;
      }

      const resolvedWeekStart = startOfWeek(
        String(attendanceResponse?.weekStart || weekStartValue || currentWeekStart())
      );
      const resolvedWeekDays = getWeekDays(resolvedWeekStart);
      const sortedEmployees = [...(Array.isArray(employeeList) ? employeeList : [])].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      );
      const records = Array.isArray(attendanceResponse?.records) ? attendanceResponse.records : [];

      setSelectedWeekStart(resolvedWeekStart);
      setEmployees(sortedEmployees);
      setAttendanceRecords(records);
      setMiscExpenses(Array.isArray(miscExpenseList) ? miscExpenseList : []);
      setDraft(buildDraft(sortedEmployees, records, resolvedWeekDays));
    } catch (err) {
      if (requestId !== refreshRequestRef.current) {
        return;
      }
      console.error(err);
      alert(err?.message || "Failed to load employee attendance.");
    } finally {
      if (requestId === refreshRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    refresh(selectedWeekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedWeekStart]);

  useEffect(() => {
    if (attendanceTableScrollRef.current) {
      attendanceTableScrollRef.current.scrollLeft = 0;
    }
  }, [selectedWeekStart]);

  useEffect(() => {
    setSalaryPaymentDraft((prev) => (prev.open ? makeSalaryPaymentDraft() : prev));
  }, [selectedWeekStart]);

  const availableEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const joiningDate = getEmployeeJoiningDate(employee);
      return !joiningDate || joiningDate <= selectedWeekEnd;
    });
  }, [employees, selectedWeekEnd]);

  const activeEmployees = useMemo(
    () => availableEmployees.filter((employee) => isEmployeeActive(employee)),
    [availableEmployees]
  );

  const inactiveEmployees = useMemo(
    () => availableEmployees.filter((employee) => !isEmployeeActive(employee)),
    [availableEmployees]
  );

  const filterEmployeeList = useCallback((employeeList) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return employeeList;
    return employeeList.filter((employee) => {
      const name = String(employee?.name || "").toLowerCase();
      const salary = String(employee?.perDaySalary ?? "").toLowerCase();
      const joiningDate = String(employee?.joiningDate || "").toLowerCase();
      const status = isEmployeeActive(employee) ? "active" : "inactive";
      return name.includes(q) || salary.includes(q) || joiningDate.includes(q) || status.includes(q);
    });
  }, [query]);

  const filteredActiveEmployees = useMemo(() => filterEmployeeList(activeEmployees), [activeEmployees, filterEmployeeList]);
  const filteredInactiveEmployees = useMemo(
    () => filterEmployeeList(inactiveEmployees),
    [filterEmployeeList, inactiveEmployees]
  );

  const salaryPaymentsByEmployee = useMemo(() => {
    const map = new Map();
    for (const entry of Array.isArray(miscExpenses) ? miscExpenses : []) {
      const title = String(entry?.title || "").trim().toLowerCase();
      const category = String(entry?.category || "").trim().toLowerCase();
      const employeeNameKey = normalizeEmployeeName(entry?.employeeName);
      if (title !== SALARY_EXPENSE_TITLE.toLowerCase() || category !== SALARY_EXPENSE_CATEGORY.toLowerCase() || !employeeNameKey) {
        continue;
      }
      if (!isYmdWithinRange(entry?.date, selectedWeekStart, selectedWeekEnd)) {
        continue;
      }
      if (!map.has(employeeNameKey)) {
        map.set(employeeNameKey, { paidTotal: 0, entries: [] });
      }
      const bucket = map.get(employeeNameKey);
      const amount = Number(entry?.amount) || 0;
      bucket.paidTotal += amount;
      bucket.entries.push(entry);
    }
    for (const bucket of map.values()) {
      bucket.paidTotal = +Number(bucket.paidTotal || 0).toFixed(2);
      bucket.entries.sort((a, b) => String(b?.date || "").localeCompare(String(a?.date || "")));
    }
    return map;
  }, [miscExpenses, selectedWeekEnd, selectedWeekStart]);

  const salaryPaymentsByEmployeeDay = useMemo(() => {
    const result = new Map();

    for (const [employeeNameKey, stats] of salaryPaymentsByEmployee.entries()) {
      const dayMap = new Map();
      for (const entry of Array.isArray(stats?.entries) ? stats.entries : []) {
        const date = normalizeYmd(entry?.date);
        if (!date) continue;
        if (!dayMap.has(date)) {
          dayMap.set(date, { paidAmount: 0, entries: [] });
        }
        const bucket = dayMap.get(date);
        bucket.paidAmount += Number(entry?.amount) || 0;
        bucket.entries.push(entry);
      }
      for (const bucket of dayMap.values()) {
        bucket.paidAmount = +Number(bucket.paidAmount || 0).toFixed(2);
      }
      result.set(employeeNameKey, dayMap);
    }

    return result;
  }, [salaryPaymentsByEmployee]);

  const salaryPaidDayMap = useMemo(() => {
    const result = new Map();

    for (const employee of availableEmployees) {
      const employeeId = String(employee?.id || "");
      if (!employeeId) continue;

      const employeeNameKey = normalizeEmployeeName(employee?.name);
      const paymentDayMap = salaryPaymentsByEmployeeDay.get(employeeNameKey) || new Map();
      const joiningDate = getEmployeeJoiningDate(employee);
      const dayMap = new Map();

      for (const day of weekDays) {
        const locked = Boolean(joiningDate && day.date < joiningDate);
        const savedRecord = attendanceRecordMap.get(`${employeeId}|${day.date}`);
        const effectiveRate =
          savedRecord?.perDaySalary === null || savedRecord?.perDaySalary === undefined
            ? Number(employee?.perDaySalary) || 0
            : Number(savedRecord.perDaySalary) || 0;
        const attendanceValue = locked ? 0 : parseAttendanceValue(savedRecord?.attendanceValue);
        const daySalaryAmount = +(attendanceValue * effectiveRate).toFixed(2);
        const paidStats = paymentDayMap.get(day.date);
        const paidAmount = +(Number(paidStats?.paidAmount) || 0).toFixed(2);
        const remainingAmount = +Math.max(0, daySalaryAmount - paidAmount).toFixed(2);
        const paymentCount = Array.isArray(paidStats?.entries) ? paidStats.entries.length : 0;
        let paymentStatus = locked ? "locked" : "none";

        if (!locked && daySalaryAmount > 0) {
          if (paymentCount > 0 && paidAmount >= daySalaryAmount - 0.0001) {
            paymentStatus = "paid";
          } else if (paymentCount > 0) {
            paymentStatus = "partial";
          } else {
            paymentStatus = "unpaid";
          }
        }

        dayMap.set(day.date, {
          paymentStatus,
          paidAmount,
          remainingAmount,
          daySalaryAmount,
          attendanceValue,
          locked,
          paymentCount,
        });
      }

      result.set(employeeId, dayMap);
    }

    return result;
  }, [availableEmployees, attendanceRecordMap, salaryPaymentsByEmployeeDay, weekDays]);

  const buildEmployeeRows = useCallback((employeeList) => {
    return employeeList.map((employee) => {
      let attendanceTotal = 0;
      let salaryTotal = 0;

      for (const day of weekDays) {
        const attendanceValue = isAttendanceDayLocked(employee, day.date)
          ? 0
          : parseAttendanceValue(draft?.[employee.id]?.[day.date]);
        const savedRecord = attendanceRecordMap.get(`${employee.id}|${day.date}`);
        const effectiveRate =
          savedRecord?.perDaySalary === null || savedRecord?.perDaySalary === undefined
            ? Number(employee?.perDaySalary) || 0
            : Number(savedRecord.perDaySalary) || 0;

        attendanceTotal += attendanceValue;
        salaryTotal += attendanceValue * effectiveRate;
      }

      const paymentStats = salaryPaymentsByEmployee.get(normalizeEmployeeName(employee?.name));
      const employeeDayStatuses = salaryPaidDayMap.get(employee.id) || new Map();
      const salaryPaidTotal = +Array.from(employeeDayStatuses.values())
        .reduce((sum, dayStatus) => sum + (Number(dayStatus?.paidAmount) || 0), 0)
        .toFixed(2);
      const salaryPending = +Math.max(0, salaryTotal - salaryPaidTotal).toFixed(2);
      const salaryAdvance = +Math.max(0, salaryPaidTotal - salaryTotal).toFixed(2);
      const hasPayableDays = Array.from(employeeDayStatuses.values()).some(
        (dayStatus) =>
          !dayStatus?.locked &&
          Number(dayStatus?.paymentCount || 0) === 0 &&
          Number(dayStatus?.daySalaryAmount || 0) > 0 &&
          Number(dayStatus?.daySalaryAmount || 0) > 0
      );

      return {
        employee,
        attendanceTotal: +attendanceTotal.toFixed(2),
        salaryTotal: +salaryTotal.toFixed(2),
        salaryPaidTotal,
        salaryPending,
        salaryAdvance,
        paymentCount: Array.isArray(paymentStats?.entries) ? paymentStats.entries.length : 0,
        latestPaymentDate: paymentStats?.entries?.[0]?.date || "",
        hasPayableDays,
      };
    });
  }, [attendanceRecordMap, draft, salaryPaidDayMap, salaryPaymentsByEmployee, weekDays]);

  const activeEmployeeRows = useMemo(
    () => buildEmployeeRows(filteredActiveEmployees),
    [buildEmployeeRows, filteredActiveEmployees]
  );

  const inactiveEmployeeRows = useMemo(
    () => buildEmployeeRows(filteredInactiveEmployees),
    [buildEmployeeRows, filteredInactiveEmployees]
  );

  const buildSummary = useCallback((rows) => {
    let attendanceTotal = 0;
    let salaryTotal = 0;
    let salaryPaidTotal = 0;
    let salaryPendingTotal = 0;
    for (const row of rows) {
      attendanceTotal += Number(row?.attendanceTotal) || 0;
      salaryTotal += Number(row?.salaryTotal) || 0;
      salaryPaidTotal += Number(row?.salaryPaidTotal) || 0;
      salaryPendingTotal += Number(row?.salaryPending) || 0;
    }
    return {
      employees: rows.length,
      days: weekDays.length,
      attendanceTotal: +attendanceTotal.toFixed(2),
      salaryTotal: +salaryTotal.toFixed(2),
      salaryPaidTotal: +salaryPaidTotal.toFixed(2),
      salaryPendingTotal: +salaryPendingTotal.toFixed(2),
    };
  }, [weekDays.length]);

  const activeSummary = useMemo(() => buildSummary(activeEmployeeRows), [activeEmployeeRows, buildSummary]);
  const inactiveSummary = useMemo(() => buildSummary(inactiveEmployeeRows), [buildSummary, inactiveEmployeeRows]);

  const buildDayTotals = useCallback((employeeList) => {
    return weekDays.map((day) => {
      let attendanceTotal = 0;
      let salaryTotal = 0;

      for (const employee of employeeList) {
        const attendanceValue = isAttendanceDayLocked(employee, day.date)
          ? 0
          : parseAttendanceValue(draft?.[employee.id]?.[day.date]);
        const savedRecord = attendanceRecordMap.get(`${employee.id}|${day.date}`);
        const effectiveRate =
          savedRecord?.perDaySalary === null || savedRecord?.perDaySalary === undefined
            ? Number(employee?.perDaySalary) || 0
            : Number(savedRecord.perDaySalary) || 0;

        attendanceTotal += attendanceValue;
        salaryTotal += attendanceValue * effectiveRate;
      }

      return {
        date: day.date,
        attendanceTotal: +attendanceTotal.toFixed(2),
        salaryTotal: +salaryTotal.toFixed(2),
      };
    });
  }, [attendanceRecordMap, draft, weekDays]);

  const activeDayTotals = useMemo(
    () => buildDayTotals(filteredActiveEmployees),
    [buildDayTotals, filteredActiveEmployees]
  );

  const inactiveDayTotals = useMemo(
    () => buildDayTotals(filteredInactiveEmployees),
    [buildDayTotals, filteredInactiveEmployees]
  );

  const isInactiveTab = attendanceTab === "inactive";
  const currentEmployeeRows = isInactiveTab ? inactiveEmployeeRows : activeEmployeeRows;
  const currentSummary = isInactiveTab ? inactiveSummary : activeSummary;
  const currentDayTotals = isInactiveTab ? inactiveDayTotals : activeDayTotals;
  const currentVisibleEmployees = isInactiveTab ? filteredInactiveEmployees : filteredActiveEmployees;
  const currentEmployeePool = isInactiveTab ? inactiveEmployees : activeEmployees;

  const handleCellChange = (employeeId, date, value) => {
    const raw = String(value ?? "");
    if (raw !== "" && (!Number.isFinite(Number(raw)) || Number(raw) < 0)) {
      return;
    }

    setDraft((prev) => ({
      ...prev,
      [employeeId]: {
        ...(prev?.[employeeId] || {}),
        [date]: raw,
      },
    }));
  };

  const handleCellBlur = (employeeId, date) => {
    setDraft((prev) => ({
      ...prev,
      [employeeId]: {
        ...(prev?.[employeeId] || {}),
        [date]: formatUnits(parseAttendanceValue(prev?.[employeeId]?.[date])),
      },
    }));
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    if (activeEmployees.length === 0) {
      alert("No employees are available for the selected week. Check the employee joining dates.");
      return;
    }

    const entries = [];
    for (const employee of activeEmployees) {
      for (const day of weekDays) {
        const dayStatus = salaryPaidDayMap.get(employee.id)?.get(day.date);
        if (isAttendanceDayLocked(employee, day.date) || Number(dayStatus?.paymentCount || 0) > 0) {
          continue;
        }
        entries.push({
          employeeId: employee.id,
          date: day.date,
          attendanceValue: parseAttendanceValue(draft?.[employee.id]?.[day.date]),
        });
      }
    }

    setSaving(true);
    try {
      const response = await saveEmployeeAttendanceWeek({
        weekStart: selectedWeekStart,
        entries,
      });
      const records = Array.isArray(response?.records) ? response.records : [];
      const resolvedWeekStart = startOfWeek(
        String(response?.weekStart || selectedWeekStart || currentWeekStart())
      );
      const resolvedWeekDays = getWeekDays(resolvedWeekStart);

      setSelectedWeekStart(resolvedWeekStart);
      setAttendanceRecords(records);
      setDraft(buildDraft(availableEmployees, records, resolvedWeekDays));
      alert("Employee attendance saved.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save employee attendance.");
    } finally {
      setSaving(false);
    }
  };

  const shiftWeek = (offsetDays) => {
    setSelectedWeekStart((prev) => startOfWeek(addDaysYmd(prev, offsetDays)));
  };

  const salaryPaymentCurrentRow = useMemo(() => {
    if (!salaryPaymentDraft.employeeId) return null;
    return activeEmployeeRows.find((row) => String(row?.employee?.id || "") === String(salaryPaymentDraft.employeeId)) || null;
  }, [activeEmployeeRows, salaryPaymentDraft.employeeId]);

  const getEmployeeSalaryWeekDateRows = useCallback((employeeId) => {
    const dayStatuses = salaryPaidDayMap.get(employeeId) || new Map();
    return weekDays
      .map((day) => {
        const dayStatus = dayStatuses.get(day.date);
        if (!dayStatus || dayStatus.locked || Number(dayStatus.daySalaryAmount || 0) <= 0) {
          return null;
        }
        return {
          date: day.date,
          weekdayFull: day.weekdayFull,
          attendanceValue: Number(dayStatus.attendanceValue) || 0,
          daySalaryAmount: Number(dayStatus.daySalaryAmount) || 0,
          paidAmount: Number(dayStatus.paidAmount) || 0,
          paymentCount: Number(dayStatus.paymentCount) || 0,
          selectable: Number(dayStatus.paymentCount || 0) === 0,
        };
      })
      .filter(Boolean);
  }, [salaryPaidDayMap, weekDays]);

  const salaryPaymentWeekDateRows = useMemo(() => {
    if (!salaryPaymentDraft.employeeId) {
      return [];
    }
    return getEmployeeSalaryWeekDateRows(salaryPaymentDraft.employeeId);
  }, [getEmployeeSalaryWeekDateRows, salaryPaymentDraft.employeeId]);

  const salaryPaymentSelectedDateSet = useMemo(() => {
    const selectedDates = Array.isArray(salaryPaymentDraft.selectedDates) ? salaryPaymentDraft.selectedDates : [];
    return new Set(selectedDates.map((value) => normalizeYmd(value)).filter(Boolean));
  }, [salaryPaymentDraft.selectedDates]);

  const salaryPaymentSelectedRows = useMemo(() => {
    return salaryPaymentWeekDateRows.filter(
      (entry) => entry.selectable && salaryPaymentSelectedDateSet.has(entry.date)
    );
  }, [salaryPaymentSelectedDateSet, salaryPaymentWeekDateRows]);

  const salaryPaymentSelectionSummary = useMemo(() => {
    return salaryPaymentSelectedRows.reduce(
      (summary, entry) => ({
        dates: summary.dates + 1,
        attendance: +(summary.attendance + (Number(entry?.attendanceValue) || 0)).toFixed(2),
        amount: +(summary.amount + (Number(entry?.daySalaryAmount) || 0)).toFixed(2),
      }),
      { dates: 0, attendance: 0, amount: 0 }
    );
  }, [salaryPaymentSelectedRows]);

  const closeSalaryPaymentModal = () => {
    if (salaryPaymentSaving) return;
    setSalaryPaymentDraft(makeSalaryPaymentDraft());
  };

  const openSalaryPaymentModal = (row) => {
    if (!row?.employee?.id) return;
    const weekDateRows = getEmployeeSalaryWeekDateRows(row.employee.id);
    const firstPayableDay = weekDateRows.find((entry) => entry.selectable);
    if (!firstPayableDay) {
      alert("No unpaid salary dates are available for this employee in the selected week.");
      return;
    }
    setSalaryPaymentDraft({
      open: true,
      employeeId: row.employee.id,
      employeeName: row.employee.name || "",
      selectedDates: weekDateRows.filter((entry) => entry.selectable).map((entry) => entry.date),
      notes: `Salary payment for week ${weekLabel}`,
    });
  };

  const saveSalaryPayment = async () => {
    if (!salaryPaymentDraft.employeeName) {
      alert("Employee is required.");
      return;
    }
    if (salaryPaymentSelectedRows.length === 0) {
      alert("Select at least one unpaid attendance date.");
      return;
    }

    setSalaryPaymentSaving(true);
    try {
      for (const entry of salaryPaymentSelectedRows) {
        const date = normalizeYmd(entry.date);
        if (!date) {
          throw new Error("One of the selected payment dates is invalid.");
        }
        if (!isYmdWithinRange(date, selectedWeekStart, selectedWeekEnd)) {
          throw new Error(
            `Payment date must be between ${formatDisplayDate(selectedWeekStart)} and ${formatDisplayDate(selectedWeekEnd)}.`
          );
        }
        if (!entry.selectable) {
          throw new Error(`Salary is already paid for ${formatDisplayDate(entry.date)}.`);
        }
        if (Number(entry.daySalaryAmount || 0) <= 0) {
          throw new Error(`Attendance is not marked for ${formatDisplayDate(entry.date)}.`);
        }

        const noteParts = [String(salaryPaymentDraft.notes || "").trim()];
        if (!noteParts[0]) {
          noteParts[0] = `Salary payment for ${formatDisplayDate(entry.date)}`;
        } else if (!noteParts[0].toLowerCase().includes(formatDisplayDate(entry.date).toLowerCase())) {
          noteParts.push(`Date: ${formatDisplayDate(entry.date)}`);
        }

        await addMiscExpense({
          date,
          title: SALARY_EXPENSE_TITLE,
          employeeName: salaryPaymentDraft.employeeName,
          category: SALARY_EXPENSE_CATEGORY,
          amount: Number(entry.daySalaryAmount) || 0,
          notes: noteParts.filter(Boolean).join(" | "),
        });
      }
      await refresh(selectedWeekStart);
      setSalaryPaymentDraft(makeSalaryPaymentDraft());
      alert(`Salary payment saved for ${salaryPaymentSelectedRows.length} date(s).`);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save salary payment.");
    } finally {
      setSalaryPaymentSaving(false);
    }
  };

  const stickyLeftBase = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    background: "rgba(255, 255, 255, 0.98)",
    boxShadow: "8px 0 16px rgba(15, 23, 42, 0.04)",
  };

  const stickyLeftSecondBase = {
    position: "sticky",
    left: NAME_COLUMN_WIDTH,
    zIndex: 3,
    background: "rgba(255, 255, 255, 0.98)",
    boxShadow: "8px 0 16px rgba(15, 23, 42, 0.04)",
  };

  const stickyRightAttendanceBase = {
    background: "rgba(239, 246, 255, 0.98)",
    boxShadow: "inset 1px 0 0 rgba(148, 163, 184, 0.18)",
  };

  const stickyRightSalaryBase = {
    background: "rgba(236, 253, 245, 0.98)",
    boxShadow: "inset 1px 0 0 rgba(148, 163, 184, 0.18)",
  };

  const stickyRightPaidBase = {
    background: "rgba(240, 253, 250, 0.98)",
    boxShadow: "inset 1px 0 0 rgba(148, 163, 184, 0.18)",
  };

  const stickyRightPendingBase = {
    background: "rgba(254, 242, 242, 0.98)",
    boxShadow: "inset 1px 0 0 rgba(148, 163, 184, 0.18)",
  };

  const stickyRightActionBase = {
    background: "rgba(248, 250, 252, 0.99)",
    boxShadow: "inset 1px 0 0 rgba(148, 163, 184, 0.18)",
  };

  if (!isAdmin) {
    return (
      <div className="stock-inward-container" style={{ paddingTop: 0 }}>
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">Employee Attendance</h2>
          </div>
          <div className="inward-form">
            <div className="empty-state" style={{ padding: "16px" }}>
              You do not have access to this page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="stock-inward-container employee-attendance-page"
      style={{
        paddingTop: 0,
        overflowX: "clip",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <div
        className="form-card"
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div className="card-header">
          <h2 className="card-title">Employee Attendance</h2>
          <span className="entry-count">Admin Only</span>
        </div>
        <div className="inward-form">
          <div className="form-grid" style={{ alignItems: "end" }}>
            <div className="field fl-field">
              <input
                id="employee-attendance-week-start"
                className="fl-input input-field"
                type="date"
                value={selectedWeekStart}
                onChange={(event) => setSelectedWeekStart(startOfWeek(event.target.value || todayYmd()))}
                placeholder=" "
                required
              />
              <label htmlFor="employee-attendance-week-start" className="fl-label">
                Week Start Date
              </label>
            </div>
            <div className="field">
              <div
                style={{
                  minHeight: 46,
                  display: "flex",
                  alignItems: "center",
                  padding: "0.7rem 0.95rem",
                  borderRadius: 12,
                  border: "1px solid rgba(148, 163, 184, 0.25)",
                  background: "rgba(248, 250, 252, 0.92)",
                  fontWeight: 800,
                  color: "#0f172a",
                }}
              >
                {weekLabel}
              </div>
            </div>
            <div className="field full">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  type="search"
                  className="input-field"
                  placeholder="Search employee name, salary, joining date or status..."
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  style={{ maxWidth: 280 }}
                />
                <button type="button" className="btn-secondary" onClick={() => refresh(selectedWeekStart)} disabled={loading}>
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, color: "#64748b", fontSize: "0.92rem" }}>
            {isInactiveTab ? (
              <>Inactive employees are shown here as view-only with their historical attendance and salary data.</>
            ) : (
              <>
                Update week-wise attendance for every employee. Monday to Sunday is shown for the selected week. Enter{" "}
                <strong>0</strong> for absent days.
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className="entries-card"
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div className="card-header" style={{ borderBottom: "none", gap: 12, flexWrap: "wrap" }}>
          <h2 className="card-title">Attendance Summary</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              className={attendanceTab === "active" ? "btn-primary" : "btn-secondary"}
              onClick={() => setAttendanceTab("active")}
              disabled={loading || saving || salaryPaymentSaving}
              style={{ minWidth: 0, padding: "0.45rem 0.85rem" }}
            >
              Active Employees
            </button>
            <button
              type="button"
              className={attendanceTab === "inactive" ? "btn-primary" : "btn-secondary"}
              onClick={() => setAttendanceTab("inactive")}
              disabled={loading || saving || salaryPaymentSaving}
              style={{ minWidth: 0, padding: "0.45rem 0.85rem" }}
            >
              Inactive Employees
            </button>
            <span className="entry-count">{weekLabel}</span>
          </div>
        </div>

        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <div className="meta-item">
              <span className="meta-label">Employees</span>
              <span className="meta-value">{currentSummary.employees}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Days In Week</span>
              <span className="meta-value">{currentSummary.days}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Total Attendance</span>
              <span className="meta-value">{formatUnits(currentSummary.attendanceTotal)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Total Salary</span>
              <span className="meta-value">Rs {formatMoney(currentSummary.salaryTotal)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Salary Paid</span>
              <span className="meta-value">Rs {formatMoney(currentSummary.salaryPaidTotal)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Salary Pending</span>
              <span className="meta-value">Rs {formatMoney(currentSummary.salaryPendingTotal)}</span>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "0 16px 10px",
            color: "#64748b",
            fontSize: "0.9rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!isInactiveTab ? (
              <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                {saving ? "Saving..." : "Save Attendance"}
              </button>
            ) : null}
            <button type="button" className="btn-secondary" onClick={() => shiftWeek(-7)} disabled={loading || saving}>
              Previous Week
            </button>
            <button type="button" className="btn-secondary" onClick={() => shiftWeek(7)} disabled={loading || saving}>
              Next Week
            </button>
          </div>
        </div>

        <div
          ref={attendanceTableScrollRef}
          className="entries-table-container"
          style={{
            margin: "0 16px 16px",
            padding: "16px",
            paddingBottom: 16,
            width: "calc(100vw - 32px)",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            boxSizing: "border-box",
            overscrollBehaviorX: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            className="entries-table"
            style={{
              width: `${tableMinWidth}px`,
              minWidth: `${tableMinWidth}px`,
              maxWidth: "none",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    minWidth: NAME_COLUMN_WIDTH,
                    width: NAME_COLUMN_WIDTH,
                    ...stickyLeftBase,
                    zIndex: 6,
                    background: "linear-gradient(135deg, #475569, #334155)",
                  }}
                >
                  Employee Name
                </th>
                <th
                  className="text-right"
                  style={{
                    minWidth: RATE_COLUMN_WIDTH,
                    width: RATE_COLUMN_WIDTH,
                    ...stickyLeftSecondBase,
                    zIndex: 6,
                    background: "linear-gradient(135deg, #475569, #334155)",
                  }}
                >
                  Per Day Salary
                </th>
                {weekDays.map((day) => (
                  <th
                    key={day.date}
                    className="text-right"
                    style={{
                      minWidth: DATE_COLUMN_WIDTH,
                      width: DATE_COLUMN_WIDTH,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                      <span>{day.weekdayFull}</span>
                      <span style={{ fontSize: 11, opacity: 0.8 }}>{formatDisplayDate(day.date)}</span>
                    </div>
                  </th>
                ))}
                <th
                  className="text-right"
                  style={{
                    minWidth: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                    width: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                    ...stickyRightAttendanceBase,
                    zIndex: 6,
                    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
                    color: "#ffffff",
                  }}
                >
                  Total Attendance
                </th>
                <th
                  className="text-right"
                  style={{
                    minWidth: TOTAL_SALARY_COLUMN_WIDTH,
                    width: TOTAL_SALARY_COLUMN_WIDTH,
                    ...stickyRightSalaryBase,
                    zIndex: 7,
                    background: "linear-gradient(135deg, #0f766e, #059669)",
                    color: "#ffffff",
                  }}
                >
                  Total Salary
                </th>
                <th
                  className="text-right"
                  style={{
                    minWidth: SALARY_PAID_COLUMN_WIDTH,
                    width: SALARY_PAID_COLUMN_WIDTH,
                    ...stickyRightPaidBase,
                    zIndex: 7,
                    background: "linear-gradient(135deg, #0f766e, #10b981)",
                    color: "#ffffff",
                  }}
                >
                  Salary Paid
                </th>
                <th
                  className="text-right"
                  style={{
                    minWidth: PENDING_SALARY_COLUMN_WIDTH,
                    width: PENDING_SALARY_COLUMN_WIDTH,
                    ...stickyRightPendingBase,
                    zIndex: 7,
                    background: "linear-gradient(135deg, #b91c1c, #ef4444)",
                    color: "#ffffff",
                  }}
                >
                  Pending
                </th>
                <th
                  style={{
                    minWidth: PAYMENT_ACTION_COLUMN_WIDTH,
                    width: PAYMENT_ACTION_COLUMN_WIDTH,
                    ...stickyRightActionBase,
                    zIndex: 8,
                    background: "linear-gradient(135deg, #475569, #334155)",
                    color: "#ffffff",
                  }}
                >
                  Save / View
                </th>
              </tr>
            </thead>
            <tbody>
              {currentEmployeeRows.length === 0 ? (
                <tr>
                  <td colSpan={weekDays.length + 7} className="empty-state" style={{ padding: 16 }}>
                    {currentEmployeePool.length === 0
                      ? isInactiveTab
                        ? "No inactive employees are available for the selected week."
                        : "No active employees are available for the selected week. Add employees with the correct joining date first."
                      : employees.length === 0
                      ? "No employees found. Add employees in Employee Master first."
                      : currentVisibleEmployees.length === 0
                        ? "No employees match the current search."
                        : "No employee rows are available."}
                  </td>
                </tr>
              ) : (
                currentEmployeeRows.map((row) => {
                  const joiningDate = getEmployeeJoiningDate(row.employee);
                  const employeeDayStatuses = salaryPaidDayMap.get(row.employee.id) || new Map();
                  const inactiveRow = !isEmployeeActive(row.employee);
                  return (
                  <tr key={row.employee.id}>
                    <td
                      style={{
                        fontWeight: 800,
                        minWidth: NAME_COLUMN_WIDTH,
                        width: NAME_COLUMN_WIDTH,
                        ...stickyLeftBase,
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span>{row.employee.name}</span>
                        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                          Joining {joiningDate ? formatDisplayDate(joiningDate) : "-"}
                        </span>
                      </div>
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 800,
                        color: "#0f766e",
                        minWidth: RATE_COLUMN_WIDTH,
                        width: RATE_COLUMN_WIDTH,
                        ...stickyLeftSecondBase,
                      }}
                    >
                      Rs {formatMoney(row.employee.perDaySalary)}
                    </td>
                    {weekDays.map((day) => {
                      const dayStatus = employeeDayStatuses.get(day.date) || {
                        paymentStatus: "none",
                        paidAmount: 0,
                        daySalaryAmount: 0,
                        attendanceValue: 0,
                        locked: false,
                      };
                      const locked = Boolean(dayStatus.locked);
                      const salaryPaidLocked = Number(dayStatus.paymentCount || 0) > 0;
                      const savedAttendanceValue =
                        dayStatus.attendanceValue === null || dayStatus.attendanceValue === undefined
                          ? attendanceRecordMap.get(`${row.employee.id}|${day.date}`)?.attendanceValue
                          : dayStatus.attendanceValue;
                      const readOnlyRow = inactiveRow || isInactiveTab;
                      const currentValue = locked
                        ? ""
                        : readOnlyRow
                          ? draft?.[row.employee.id]?.[day.date] ?? formatUnits(savedAttendanceValue)
                        : salaryPaidLocked
                          ? formatUnits(savedAttendanceValue)
                          : draft?.[row.employee.id]?.[day.date] ?? "0";
                      const numericValue = locked ? 0 : parseAttendanceValue(currentValue);
                      let paymentMarker = null;

                      if (locked) {
                        paymentMarker = (
                          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>Before joining</span>
                        );
                      } else if (salaryPaidLocked) {
                        if (dayStatus.paymentStatus === "paid") {
                          paymentMarker = (
                            <span style={{ fontSize: 10, color: "#166534", fontWeight: 800 }}>Salary Paid</span>
                          );
                        } else if (dayStatus.paymentStatus === "partial") {
                          paymentMarker = (
                            <span style={{ fontSize: 10, color: "#b45309", fontWeight: 800 }}>
                              Part Paid Rs {formatMoney(dayStatus.paidAmount)}
                            </span>
                          );
                        } else if (dayStatus.paymentStatus === "unpaid") {
                          paymentMarker = (
                            <span style={{ fontSize: 10, color: "#b91c1c", fontWeight: 800 }}>Salary Unpaid</span>
                          );
                        }
                      }

                      return (
                        <td
                          key={`${row.employee.id}-${day.date}`}
                          className="text-right"
                          style={{
                            padding: 8,
                            minWidth: DATE_COLUMN_WIDTH,
                            width: DATE_COLUMN_WIDTH,
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              inputMode="decimal"
                              className="input-field"
                              value={currentValue}
                              disabled={readOnlyRow || locked || salaryPaidLocked}
                              onChange={(event) => handleCellChange(row.employee.id, day.date, event.target.value)}
                              onBlur={() => handleCellBlur(row.employee.id, day.date)}
                              style={{
                                textAlign: "right",
                                minWidth: 82,
                                background: locked
                                  ? "rgba(226, 232, 240, 0.85)"
                                  : readOnlyRow
                                    ? "rgba(241, 245, 249, 0.94)"
                                  : salaryPaidLocked
                                    ? "rgba(220, 252, 231, 0.92)"
                                  : numericValue > 0
                                    ? "rgba(220, 252, 231, 0.8)"
                                    : "rgba(248, 250, 252, 0.95)",
                                fontWeight: numericValue > 0 ? 800 : 600,
                                color: locked ? "#94a3b8" : numericValue > 0 ? "#166534" : "#334155",
                                cursor: readOnlyRow || locked || salaryPaidLocked ? "not-allowed" : undefined,
                              }}
                            />
                            {paymentMarker}
                          </div>
                        </td>
                      );
                    })}
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 900,
                        color: "#1d4ed8",
                        minWidth: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                        width: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                        ...stickyRightAttendanceBase,
                      }}
                    >
                      {formatUnits(row.attendanceTotal)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 900,
                        color: "#0f766e",
                        minWidth: TOTAL_SALARY_COLUMN_WIDTH,
                        width: TOTAL_SALARY_COLUMN_WIDTH,
                        ...stickyRightSalaryBase,
                      }}
                    >
                      Rs {formatMoney(row.salaryTotal)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 900,
                        color: "#059669",
                        minWidth: SALARY_PAID_COLUMN_WIDTH,
                        width: SALARY_PAID_COLUMN_WIDTH,
                        ...stickyRightPaidBase,
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                        <span>Rs {formatMoney(row.salaryPaidTotal)}</span>
                        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                          {row.paymentCount > 0
                            ? `${row.paymentCount} payment${row.paymentCount > 1 ? "s" : ""}`
                            : "No payment"}
                        </span>
                      </div>
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 900,
                        color: row.salaryPending > 0 ? "#b91c1c" : "#166534",
                        minWidth: PENDING_SALARY_COLUMN_WIDTH,
                        width: PENDING_SALARY_COLUMN_WIDTH,
                        ...stickyRightPendingBase,
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                        <span>Rs {formatMoney(row.salaryPending)}</span>
                        {row.salaryAdvance > 0 ? (
                          <span style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 700 }}>
                            Advance Rs {formatMoney(row.salaryAdvance)}
                          </span>
                        ) : row.latestPaymentDate ? (
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                            Last {formatDisplayDate(row.latestPaymentDate)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td
                      style={{
                        minWidth: PAYMENT_ACTION_COLUMN_WIDTH,
                        width: PAYMENT_ACTION_COLUMN_WIDTH,
                        ...stickyRightActionBase,
                      }}
                    >
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => openSalaryPaymentModal(row)}
                        disabled={inactiveRow || !row.hasPayableDays}
                        style={{
                          width: "100%",
                          justifyContent: "center",
                          padding: "0.45rem 0.7rem",
                          fontSize: "0.78rem",
                        }}
                      >
                        {inactiveRow ? "Inactive" : row.hasPayableDays ? "Add Payment" : "Paid"}
                      </button>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
            {currentEmployeeRows.length > 0 ? (
              <tfoot>
                <tr>
                  <td
                    style={{
                      fontWeight: 900,
                      minWidth: NAME_COLUMN_WIDTH,
                      width: NAME_COLUMN_WIDTH,
                      ...stickyLeftBase,
                      background: "rgba(226, 232, 240, 0.96)",
                    }}
                  >
                    Attendance Total
                  </td>
                  <td
                    className="text-right"
                    style={{
                      minWidth: RATE_COLUMN_WIDTH,
                      width: RATE_COLUMN_WIDTH,
                      ...stickyLeftSecondBase,
                      background: "rgba(226, 232, 240, 0.96)",
                    }}
                  >
                    -
                  </td>
                  {currentDayTotals.map((day) => (
                    <td
                      key={`total-${day.date}`}
                      className="text-right"
                      style={{
                        minWidth: DATE_COLUMN_WIDTH,
                        width: DATE_COLUMN_WIDTH,
                        background: "rgba(241, 245, 249, 0.92)",
                        fontWeight: 800,
                        color: "#1e293b",
                      }}
                    >
                      {formatUnits(day.attendanceTotal)}
                    </td>
                  ))}
                  <td
                    className="text-right"
                    style={{
                      fontWeight: 900,
                      color: "#1d4ed8",
                      minWidth: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                      width: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                      ...stickyRightAttendanceBase,
                    }}
                  >
                    {formatUnits(currentSummary.attendanceTotal)}
                  </td>
                  <td
                    className="text-right"
                    style={{
                      minWidth: TOTAL_SALARY_COLUMN_WIDTH,
                      width: TOTAL_SALARY_COLUMN_WIDTH,
                      ...stickyRightSalaryBase,
                    }}
                  >
                    -
                  </td>
                  <td
                    className="text-right"
                    style={{
                      minWidth: SALARY_PAID_COLUMN_WIDTH,
                      width: SALARY_PAID_COLUMN_WIDTH,
                      ...stickyRightPaidBase,
                    }}
                  >
                    -
                  </td>
                  <td
                    className="text-right"
                    style={{
                      minWidth: PENDING_SALARY_COLUMN_WIDTH,
                      width: PENDING_SALARY_COLUMN_WIDTH,
                      ...stickyRightPendingBase,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      minWidth: PAYMENT_ACTION_COLUMN_WIDTH,
                      width: PAYMENT_ACTION_COLUMN_WIDTH,
                      ...stickyRightActionBase,
                    }}
                  >
                    -
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 900,
                      minWidth: NAME_COLUMN_WIDTH,
                      width: NAME_COLUMN_WIDTH,
                      ...stickyLeftBase,
                      background: "rgba(220, 252, 231, 0.98)",
                      color: "#0f766e",
                    }}
                  >
                    Salary Total
                  </td>
                  <td
                    className="text-right"
                    style={{
                      minWidth: RATE_COLUMN_WIDTH,
                      width: RATE_COLUMN_WIDTH,
                      ...stickyLeftSecondBase,
                      background: "rgba(220, 252, 231, 0.98)",
                    }}
                  >
                    -
                  </td>
                  {currentDayTotals.map((day) => (
                    <td
                      key={`salary-total-${day.date}`}
                      className="text-right"
                      style={{
                        minWidth: DATE_COLUMN_WIDTH,
                        width: DATE_COLUMN_WIDTH,
                        background: "rgba(236, 253, 245, 0.98)",
                        fontWeight: 900,
                        color: "#0f766e",
                        fontSize: "0.78rem",
                      }}
                    >
                      Rs {formatMoney(day.salaryTotal)}
                    </td>
                  ))}
                  <td
                    className="text-right"
                    style={{
                      minWidth: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                      width: TOTAL_ATTENDANCE_COLUMN_WIDTH,
                      ...stickyRightAttendanceBase,
                    }}
                  >
                    -
                  </td>
                  <td
                    className="text-right"
                    style={{
                      fontWeight: 900,
                      color: "#0f766e",
                      minWidth: TOTAL_SALARY_COLUMN_WIDTH,
                      width: TOTAL_SALARY_COLUMN_WIDTH,
                      ...stickyRightSalaryBase,
                    }}
                  >
                    Rs {formatMoney(currentSummary.salaryTotal)}
                  </td>
                  <td
                    className="text-right"
                    style={{
                      fontWeight: 900,
                      color: "#059669",
                      minWidth: SALARY_PAID_COLUMN_WIDTH,
                      width: SALARY_PAID_COLUMN_WIDTH,
                      ...stickyRightPaidBase,
                    }}
                  >
                    Rs {formatMoney(currentSummary.salaryPaidTotal)}
                  </td>
                  <td
                    className="text-right"
                    style={{
                      fontWeight: 900,
                      color: currentSummary.salaryPendingTotal > 0 ? "#b91c1c" : "#166534",
                      minWidth: PENDING_SALARY_COLUMN_WIDTH,
                      width: PENDING_SALARY_COLUMN_WIDTH,
                      ...stickyRightPendingBase,
                    }}
                  >
                    Rs {formatMoney(currentSummary.salaryPendingTotal)}
                  </td>
                  <td
                    style={{
                      minWidth: PAYMENT_ACTION_COLUMN_WIDTH,
                      width: PAYMENT_ACTION_COLUMN_WIDTH,
                      ...stickyRightActionBase,
                    }}
                  >
                    -
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>

      {salaryPaymentDraft.open ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            background: "rgba(15, 23, 42, 0.38)",
            backdropFilter: "blur(2px)",
          }}
          onClick={closeSalaryPaymentModal}
        >
          <div
            className="form-card"
            style={{
              width: "100%",
              maxWidth: 1080,
              margin: 0,
              transform: "none",
              maxHeight: "calc(100vh - 32px)",
              overflow: "hidden",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="card-header">
              <h2 className="card-title">Save Salary Payment</h2>
              <span className="entry-count">{salaryPaymentDraft.employeeName || "Employee"}</span>
            </div>
            <div
              className="inward-form"
              style={{
                display: "grid",
                gridTemplateRows: "auto auto minmax(0, 1fr)",
                gap: 16,
                maxHeight: "calc(100vh - 140px)",
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: 4,
              }}
            >
              <div style={{ color: "#475569", fontSize: "0.92rem" }}>
                Saving to <strong>Master Expense > Salary</strong> for the selected week: <strong>{weekLabel}</strong>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 16,
                  alignItems: "stretch",
                }}
              >
                {salaryPaymentCurrentRow ? (
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: 14,
                      border: "1px solid rgba(148, 163, 184, 0.22)",
                      background: "rgba(248, 250, 252, 0.92)",
                      color: "#1e293b",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: 10,
                      minWidth: 0,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Week Attendance</div>
                      <div style={{ fontWeight: 800 }}>{formatUnits(salaryPaymentCurrentRow.attendanceTotal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Week Salary</div>
                      <div style={{ fontWeight: 800 }}>Rs {formatMoney(salaryPaymentCurrentRow.salaryTotal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Week Paid</div>
                      <div style={{ fontWeight: 800, color: "#0f766e" }}>
                        Rs {formatMoney(salaryPaymentCurrentRow.salaryPaidTotal)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Week Balance</div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: Number(salaryPaymentCurrentRow.salaryPending || 0) > 0 ? "#b91c1c" : "#166534",
                        }}
                      >
                        Rs {formatMoney(salaryPaymentCurrentRow.salaryPending)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div />
                )}
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    background: "rgba(255, 255, 255, 0.78)",
                    color: "#334155",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.76rem",
                      fontWeight: 800,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: 8,
                    }}
                  >
                    Selected Payment Summary
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                      gap: 10,
                      fontSize: "0.88rem",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "rgba(248, 250, 252, 0.92)",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Selected Dates</div>
                        <div style={{ fontWeight: 800 }}>{salaryPaymentSelectionSummary.dates}</div>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "rgba(248, 250, 252, 0.92)",
                      }}
                    >
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Selected Attendance</div>
                      <div style={{ fontWeight: 800 }}>{formatUnits(salaryPaymentSelectionSummary.attendance)}</div>
                    </div>
                    <div
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "rgba(248, 250, 252, 0.92)",
                      }}
                    >
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Selected Amount</div>
                      <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
                        Rs {formatMoney(salaryPaymentSelectionSummary.amount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.35fr) minmax(360px, 0.85fr)",
                  gap: 16,
                  minHeight: 0,
                  alignItems: "stretch",
                }}
              >
                <div
                  style={{
                    borderRadius: 14,
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    background: "rgba(255, 255, 255, 0.8)",
                    overflow: "hidden",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.82rem",
                        fontWeight: 800,
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Week Attendance Dates
                    </span>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: "0.4rem 0.8rem", minHeight: 0 }}
                        onClick={() =>
                          setSalaryPaymentDraft((prev) => ({
                            ...prev,
                            selectedDates: salaryPaymentWeekDateRows
                              .filter((entry) => entry.selectable)
                              .map((entry) => entry.date),
                          }))
                        }
                        disabled={salaryPaymentSaving || salaryPaymentWeekDateRows.every((entry) => !entry.selectable)}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: "0.4rem 0.8rem", minHeight: 0 }}
                        onClick={() =>
                          setSalaryPaymentDraft((prev) => ({
                            ...prev,
                            selectedDates: [],
                          }))
                        }
                        disabled={salaryPaymentSaving || salaryPaymentSelectedRows.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    <table className="entries-table" style={{ minWidth: "100%", margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 82 }}>Pay</th>
                          <th>Date</th>
                          <th className="text-right">Attendance</th>
                          <th className="text-right">Amount</th>
                          <th className="text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salaryPaymentWeekDateRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="empty-state" style={{ padding: 14 }}>
                              No attendance dates are available for this week.
                            </td>
                          </tr>
                        ) : (
                          salaryPaymentWeekDateRows.map((entry) => (
                            <tr key={entry.date}>
                              <td>
                                {entry.selectable ? (
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: salaryPaymentSaving ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={salaryPaymentSelectedDateSet.has(entry.date)}
                                      disabled={salaryPaymentSaving}
                                      onChange={(event) =>
                                        setSalaryPaymentDraft((prev) => {
                                          const nextSet = new Set(
                                            Array.isArray(prev.selectedDates) ? prev.selectedDates : []
                                          );
                                          if (event.target.checked) {
                                            nextSet.add(entry.date);
                                          } else {
                                            nextSet.delete(entry.date);
                                          }
                                          return {
                                            ...prev,
                                            selectedDates: Array.from(nextSet),
                                          };
                                        })
                                      }
                                    />
                                  </label>
                                ) : (
                                  <span style={{ color: "#94a3b8", fontWeight: 800 }}>-</span>
                                )}
                              </td>
                              <td>
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  <span>{formatDisplayDate(entry.date)}</span>
                                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                                    {entry.weekdayFull}
                                  </span>
                                </div>
                              </td>
                              <td className="text-right">{formatUnits(entry.attendanceValue)}</td>
                              <td className="text-right">{`Rs ${formatMoney(entry.daySalaryAmount)}`}</td>
                              <td className="text-right">
                                <span
                                  style={{
                                    fontWeight: 800,
                                    color: !entry.selectable
                                      ? "#b91c1c"
                                      : salaryPaymentSelectedDateSet.has(entry.date)
                                        ? "#166534"
                                        : "#64748b",
                                  }}
                                >
                                  {!entry.selectable
                                    ? "Paid"
                                    : salaryPaymentSelectedDateSet.has(entry.date)
                                      ? "Selected"
                                      : "Skipped"}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div
                  style={{
                    minWidth: 0,
                    borderRadius: 14,
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    background: "rgba(255, 255, 255, 0.86)",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
                      fontSize: "0.82rem",
                      fontWeight: 800,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Payment Entry
                  </div>
                  <div style={{ padding: 16, overflowY: "auto" }}>
                    <div className="form-grid" style={{ gridTemplateColumns: "1fr", gap: 12 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 14px",
                            borderRadius: 12,
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                            background: "rgba(248, 250, 252, 0.9)",
                          }}
                        >
                          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Selected Dates</div>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>{salaryPaymentSelectionSummary.dates}</div>
                        </div>
                        <div
                          style={{
                            padding: "12px 14px",
                            borderRadius: 12,
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                            background: "rgba(248, 250, 252, 0.9)",
                          }}
                        >
                          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>Total Amount</div>
                          <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
                            Rs {formatMoney(salaryPaymentSelectionSummary.amount)}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px dashed rgba(59, 130, 246, 0.28)",
                          background: "rgba(239, 246, 255, 0.78)",
                          color: "#1e3a8a",
                          fontSize: "0.88rem",
                          fontWeight: 700,
                          lineHeight: 1.5,
                        }}
                      >
                        Checked dates will be saved as salary payments. Uncheck any date that should stay unpaid for this week.
                      </div>
                      <div className="field full fl-field">
                        <textarea
                          className="fl-input input-field"
                          value={salaryPaymentDraft.notes}
                          onChange={(event) =>
                            setSalaryPaymentDraft((prev) => ({ ...prev, notes: event.target.value }))
                          }
                          placeholder=" "
                          rows={5}
                          style={{ resize: "vertical", minHeight: 132 }}
                        />
                        <label className="fl-label">Notes</label>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: "auto",
                      padding: "14px 16px 16px",
                      borderTop: "1px solid rgba(148, 163, 184, 0.16)",
                      background:
                        "linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(248,250,252,0.98) 100%)",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={closeSalaryPaymentModal}
                      disabled={salaryPaymentSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={saveSalaryPayment}
                      disabled={salaryPaymentSaving}
                    >
                      {salaryPaymentSaving ? "Saving..." : "Save Payment"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
